import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import DownloadIcon from '@mui/icons-material/DownloadRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { NewLayout } from '../components/newLayout/NewLayout'
import { newThemeObject } from '../newTheme'
import { tokens } from '../styles/tokens'
import CardWrapper from '../components/common/CardWrapper'
import { LoadingIndicator } from '../components/common/LoadingIndicator'
import { ColoredIconButton } from '../components/common/ColoredIconButton'
import { ActionFailedAlert } from '../components/dialogs/ActionFailedAlert'
import { ScanUploadDialog } from '../components/documents/ScanUploadDialog'
import { ClientDocument } from '../api/documentApi'
import { useDocumentApi } from '../hooks/useDocumentApi'
import { useClientApi } from '../hooks/useClientApi'
import { useSelector } from '../redux/hooks/useSelector'
import { isDistributorUser } from '../utils/distributor'
import { Pages } from '../router/router'

const useStyles = makeStyles(theme => ({
  paper: {
    overflowY: 'auto',
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionHeader: {
    background: `linear-gradient(135deg, ${tokens.color.primary[800]}, ${tokens.color.primary[600]})`,
    margin: '-16px -16px 16px',
    padding: '12px 16px',
    borderRadius: tokens.radius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: '48px',
  },
  sectionHeaderTitle: {
    color: tokens.color.white,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  headerButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  headerButton: {
    color: tokens.color.white,
    borderColor: tokens.color.white,
    '&:hover': {
      borderColor: tokens.color.white,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  table: {
    minWidth: 800,
  },
  tableHead: {
    ...newThemeObject.table.tableHead,
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  tableContentFirst: {
    ...newThemeObject.table.tableContentFirst,
  },
  tableRow: {
    transition: `background-color ${tokens.transition.fast}`,
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: tokens.color.neutral[200],
    },
    '&:hover': {
      backgroundColor: tokens.color.primary[50],
    },
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
  },
  empty: {
    margin: theme.spacing(2),
    fontSize: '0.8rem',
    color: tokens.color.neutral[500],
  },
}))

const STATE_LABELS: Record<string, string> = {
  GENERATED: 'Vygenerováno',
  SIGNED: 'Podepsáno',
  ARCHIVED: 'Archivováno',
  CANCELLED: 'Zrušeno',
}

const formatDate = (value: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return value
  return date.toLocaleDateString('cs-CZ') + ' ' + date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
}

export const ClientDetailDocuments = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const { clientId } = useParams<{ clientId: string }>()

  const client = useSelector(state => state.clients.client)
  const user = useSelector(state => state.auth.user)
  const loaders = useSelector(state => state.loading.isLoading)
  const isLoading = !!loaders.find(loader => loader === 'getClientDocuments')

  const { getClient } = useClientApi()
  const { getClientDocuments, downloadDocument } = useDocumentApi()

  const [documents, setDocuments] = useState<ClientDocument[]>([])
  const [uploadOpen, setUploadOpen] = useState(false)

  const isReadOnly = isDistributorUser(user)

  useEffect(() => {
    if (!client || client.clientId !== Number(clientId)) {
      getClient(Number(clientId))
    }
  }, [clientId])

  const loadDocuments = useCallback(() => {
    getClientDocuments(Number(clientId)).then(items => setDocuments(items))
  }, [clientId])

  useEffect(() => {
    loadDocuments()
  }, [clientId])

  return (
    <NewLayout pageName="DOCUMENTS">
      {isLoading && <LoadingIndicator />}
      {!isLoading && (
        <TableContainer component={CardWrapper} className={classes.paper}>
          <div className={classes.sectionHeader}>
            <p className={classes.sectionHeaderTitle}>{t('DOCUMENTS')}</p>
            {!isReadOnly && (
              <div className={classes.headerButtons}>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.headerButton}
                  startIcon={<UploadFileRoundedIcon />}
                  onClick={() => setUploadOpen(true)}
                >
                  {t('UPLOAD_SCAN')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.headerButton}
                  onClick={() => history.push(`${Pages.Documents}?clientId=${clientId}`)}
                >
                  {t('GENERATE')}
                </Button>
              </div>
            )}
          </div>
          <Table className={classes.table} size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                  <p className={classes.tableHeadChildrenText}>{t('FILE')}</p>
                </TableCell>
                <TableCell className={classes.tableHeadChildren}>
                  <p className={classes.tableHeadChildrenText}>{t('TYPE')}</p>
                </TableCell>
                <TableCell className={classes.tableHeadChildren}>
                  <p className={classes.tableHeadChildrenText}>{t('TEMPLATE')}</p>
                </TableCell>
                <TableCell className={classes.tableHeadChildren}>
                  <p className={classes.tableHeadChildrenText}>{t('STATE')}</p>
                </TableCell>
                <TableCell className={classes.tableHeadChildren}>
                  <p className={classes.tableHeadChildrenText}>{t('VERSION')}</p>
                </TableCell>
                <TableCell className={classes.tableHeadChildren}>
                  <p className={classes.tableHeadChildrenText}>{t('DATE')}</p>
                </TableCell>
                <TableCell className={classes.tableHeadChildren}>
                  <p className={classes.tableHeadChildrenText}>{t('AUTHOR')}</p>
                </TableCell>
                <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenLast])} />
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map(document => (
                <TableRow key={document.clientDocumentId} className={classes.tableRow}>
                  <TableCell className={classes.tableContentFirst}>
                    <p className={classes.text}>{document.fileName}</p>
                  </TableCell>
                  <TableCell className={classes.tableContent}>
                    <p className={classes.text}>{document.documentType}</p>
                  </TableCell>
                  <TableCell className={classes.tableContent}>
                    <p className={classes.text}>{document.templateKey}</p>
                  </TableCell>
                  <TableCell className={classes.tableContent}>
                    <p className={classes.text}>{STATE_LABELS[document.state] || document.state}</p>
                  </TableCell>
                  <TableCell className={classes.tableContent}>
                    <p className={classes.text}>{document.version}</p>
                  </TableCell>
                  <TableCell className={classes.tableContent}>
                    <p className={classes.text}>{formatDate(document.createdAt)}</p>
                  </TableCell>
                  <TableCell className={classes.tableContent}>
                    <p className={classes.text}>{document.createdByName}</p>
                  </TableCell>
                  <TableCell className={classes.tableContent}>
                    <ColoredIconButton
                      size="small"
                      icon={<DownloadIcon />}
                      title={t('DOWNLOAD')}
                      onClick={() => downloadDocument(document.clientDocumentId)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {documents.length === 0 && <p className={classes.empty}>{t('NO_DOCUMENTS')}</p>}
        </TableContainer>
      )}

      <ScanUploadDialog
        open={uploadOpen}
        clientId={Number(clientId)}
        documents={documents}
        onClose={() => setUploadOpen(false)}
        onUploaded={loadDocuments}
      />
      <ActionFailedAlert handleClose={() => {}} />
    </NewLayout>
  )
}
